import { useEffect, useRef, useState } from 'react';

export interface CursorSettings {
  /** Lerp factor for ring smooth follow (0.05 - 0.5). Higher = snappier, lower = smoother. Default: 0.15 */
  followSpeed?: number;
  /** Easing style preset. 'smooth' = silky luxury feel, 'snappy' = responsive tech feel, 'magnetic' = strong pull. Default: 'smooth' */
  easingStyle?: 'smooth' | 'snappy' | 'magnetic';
  /** Dot size in pixels. Default: 8 */
  dotSize?: number;
  /** Base ring size in pixels. Default: 32 */
  ringSize?: number;
  /** Ring border color opacity (0-1). Default: 0.8 */
  ringOpacity?: number;
  /** Show a subtle trailing ring behind the main ring. Default: true */
  trailEnabled?: boolean;
  /** Number of trail dots. Default: 5 */
  trailCount?: number;
  /** Trail fade opacity multiplier (0-1). Default: 0.3 */
  trailOpacity?: number;
}

const PRESET_CONFIGS: Record<NonNullable<CursorSettings['easingStyle']>, { speed: number; label: string }> = {
  smooth: { speed: 0.12, label: 'Silky smooth luxury feel' },
  snappy: { speed: 0.28, label: 'Responsive modern tech feel' },
  magnetic: { speed: 1.42, label: 'Strong magnetic pull feel' },
};

const CustomCursor = ({
  followSpeed,
  easingStyle = 'smooth',
  dotSize = 8,
  ringSize = 32,
  ringOpacity = 0.8,
  trailEnabled = true,
  trailCount = 5,
  trailOpacity = 0.3,
}: CursorSettings = {}) => {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const trailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [clicking, setClicking] = useState(false);
  const [hidden, setHidden] = useState(false);

  const finalSpeed = followSpeed ?? PRESET_CONFIGS[easingStyle].speed;

  // Initialize: check device support
  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    if (!mq.matches) return;
    setEnabled(true);
  }, []);

  // Main cursor animation — runs after elements are mounted
  useEffect(() => {
    if (!enabled) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let rafId = 0;

    const trailPositions = Array.from({ length: trailCount }).map(() => ({
      x: mouseX,
      y: mouseY,
    }));

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 1px) translate(-50%, -50%)`;
    };

    const animate = () => {
      ringX += (mouseX - ringX) * finalSpeed;
      ringY += (mouseY - ringY) * finalSpeed;
      ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 1px) translate(-50%, -50%)`;

      if (trailEnabled && trailCount > 1) {
        trailPositions[0].x = ringX;
        trailPositions[0].y = ringY;

        for (let i = 1; i < trailCount; i++) {
          trailPositions[i].x += (trailPositions[i - 1].x - trailPositions[i].x) * (finalSpeed * 0.6);
          trailPositions[i].y += (trailPositions[i - 1].y - trailPositions[i].y) * (finalSpeed * 0.6);
        }

        for (let i = 1; i < trailCount; i++) {
          const el = trailRefs.current[i];
          if (el) {
            const scale = 1 - (i / trailCount) * 0.7;
            const opacity = trailOpacity * (1 - i / trailCount);
            el.style.transform = `translate3d(${trailPositions[i].x}px, ${trailPositions[i].y}px, 0px) translate(-50%, -50%) scale(${scale})`;
            el.style.opacity = String(opacity);
          }
        }
      }

      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);

    const onOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"], input, textarea, select, label, [data-cursor-hover]')) {
        setHovering(true);
      }
    };
    const onOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"], input, textarea, select, label, [data-cursor-hover]')) {
        setHovering(false);
      }
    };

    const onDown = () => setClicking(true);
    const onUp = () => setClicking(false);
    const onLeave = () => setHidden(true);
    const onEnter = () => setHidden(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver);
    window.addEventListener('mouseout', onOut);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      window.removeEventListener('mouseout', onOut);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
    };
  }, [enabled, finalSpeed, trailEnabled, trailCount, trailOpacity]);

  if (!enabled) return null;

  const dotPx = dotSize;
  const ringPx = ringSize;
  const hoverRingPx = ringSize * 1.5;
  const clickRingPx = ringSize * 0.75;

  return (
    <>
      {/* Trail rings (indices 1..trailCount-1; index 0 is the main ring) */}
      {trailEnabled &&
        Array.from({ length: trailCount }).map((_, i) =>
          i === 0 ? null : (
            <div
              key={`trail-${i}`}
              ref={(el) => {
                trailRefs.current[i] = el;
              }}
              aria-hidden="true"
              className="pointer-events-none fixed left-0 top-0 z-[9998] rounded-full border border-primary/40"
              style={{
                width: ringPx * 0.6,
                height: ringPx * 0.6,
                opacity: 0,
                willChange: 'transform',
              }}
            />
          )
        )}

      {/* Inner dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        className={`pointer-events-none fixed left-0 top-0 z-[9999] rounded-full bg-primary transition-[opacity,width,height] duration-200 ease-out ${
          hidden ? 'opacity-0' : 'opacity-100'
        } ${hovering ? 'opacity-80' : ''}`}
        style={{
          width: clicking ? dotPx * 1.5 : dotPx,
          height: clicking ? dotPx * 1.5 : dotPx,
          mixBlendMode: 'difference',
          willChange: 'transform',
        }}
      />

      {/* Outer ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        className={`pointer-events-none fixed left-0 top-0 z-[9999] rounded-full border-2 border-primary transition-[opacity,width,height,background-color,border-color] duration-300 ease-out ${
          hidden ? 'opacity-0' : 'opacity-100'
        } ${hovering ? 'border-primary bg-primary/15 backdrop-blur-sm' : ''} ${clicking ? 'bg-primary/30' : ''}`}
        style={{
          width: clicking ? clickRingPx : hovering ? hoverRingPx : ringPx,
          height: clicking ? clickRingPx : hovering ? hoverRingPx : ringPx,
          borderColor: `hsl(var(--primary) / ${ringOpacity})`,
          willChange: 'transform',
        }}
      />
    </>
  );
};

export default CustomCursor;
